import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Highlighter, StickyNote, X } from 'lucide-react';

// Floating, read-only viewer for a student's saved highlights/notes in review
// mode. Two tabs (Highlight / Ghi chú). Clicking an item scrolls the passage to
// that segment and flashes it. All DOM interaction is READ-ONLY (find text +
// scrollIntoView) plus a transient absolutely-positioned overlay for the flash —
// it NEVER mutates the passage DOM, so it cannot affect answers/explanations.
// Rendered through a portal to <body> so it floats above the exam layout.

const HL_COLORS = { 1: '#7a4d6b', 2: '#ec4899' }; // mắm tôm (level 1), hồng cánh sen (level 2)

// Find a Range for `text` inside any of the given container selectors. Tries the
// full string, then a shortened prefix (passages often re-flow whitespace).
function findTextRange(selectors, text) {
  const needleFull = (text || '').trim();
  if (!needleFull) return null;
  const candidates = [needleFull, needleFull.slice(0, 40), needleFull.slice(0, 20)].filter(Boolean);
  for (const sel of selectors) {
    const root = document.querySelector(sel);
    if (!root) continue;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    let node;
    while ((node = walker.nextNode())) {
      const content = node.textContent || '';
      for (const needle of candidates) {
        const idx = content.indexOf(needle);
        if (idx !== -1) {
          const range = document.createRange();
          range.setStart(node, idx);
          range.setEnd(node, Math.min(content.length, idx + needle.length));
          return range;
        }
      }
    }
  }
  return null;
}

// Transient flash overlay over a Range's client rects. Non-destructive: fixed
// divs on top of the page, removed after the animation.
function flashRange(range) {
  const rects = Array.from(range.getClientRects());
  if (!rects.length) return;
  const layer = document.createElement('div');
  layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:99997;';
  rects.forEach((r) => {
    const d = document.createElement('div');
    d.style.cssText = [
      'position:fixed',
      `left:${r.left}px`,
      `top:${r.top - 2}px`,
      `width:${r.width}px`,
      `height:${r.height + 4}px`,
      'background:#facc15',
      'opacity:0.8',
      'border-radius:3px',
      'transition:opacity 1.3s ease',
    ].join(';');
    layer.appendChild(d);
  });
  document.body.appendChild(layer);
  // Hold bright ~0.7s so the user clearly sees it, then fade over ~1.3s (~2s total).
  setTimeout(() => {
    Array.from(layer.children).forEach((c) => { c.style.opacity = '0'; });
  }, 700);
  setTimeout(() => layer.remove(), 2100);
}

// Does localStorage still hold this exam's highlights/notes? If so, the exam
// room's own restore paints them inline and the floating panel is redundant.
function inlineHasData(examId) {
  try {
    const has = (key) => {
      const arr = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(arr) && arr.some((x) => x && Number(x.examId) === Number(examId));
    };
    return has('ielts-highlights') || has('ielts-notes');
  } catch {
    return false;
  }
}

export default function AnnotationsPanel({
  highlights = [],
  notes = [],
  passageSelectors = ['#reading-passage-content', '#ielts-content-area'],
  currentPart,
  onGoToPart,
  examId,
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('highlights');

  const hs = (Array.isArray(highlights) ? highlights : []).filter((h) => h && (h.text || '').trim());
  const ns = (Array.isArray(notes) ? notes : []).filter((n) => n && ((n.text || '').trim() || (n.selectedText || '').trim()));
  if (hs.length === 0 && ns.length === 0) return null;
  // Only surface the panel when the room isn't already showing them inline.
  if (inlineHasData(examId)) return null;

  const total = hs.length + ns.length;
  const byPart = (a, b) => (a.part || 0) - (b.part || 0);
  const activeTab = tab === 'notes' && ns.length > 0 ? 'notes' : hs.length > 0 ? 'highlights' : 'notes';

  const goTo = (text, part) => {
    const run = () => {
      const range = findTextRange(passageSelectors, text);
      if (!range) return;
      const el = range.startContainer.parentElement;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => flashRange(range), 350);
    };
    if (onGoToPart && part && Number(part) !== Number(currentPart)) {
      onGoToPart(Number(part));
      setTimeout(run, 500); // wait for the part's passage to render
    } else {
      run();
    }
  };

  const TabBtn = ({ id, label, count, Icon }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
        activeTab === id ? 'border-[#0096b1] text-[#0096b1]' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label} <span className="text-xs font-normal">({count})</span>
    </button>
  );

  return createPortal(
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-[176px] right-6 z-[99998] flex items-center gap-2 rounded-full bg-[#0096b1] px-4 py-3 text-white shadow-lg transition-colors hover:bg-[#007d93]"
        title="Xem highlight & ghi chú của bạn"
      >
        <Highlighter className="h-5 w-5" />
        <span className="hidden text-sm font-semibold sm:inline">Highlight &amp; ghi chú</span>
        <span className="rounded-full bg-white/25 px-2 py-0.5 text-xs font-bold">{total}</span>
      </button>

      {open && (
        <div className="fixed bottom-[240px] right-6 z-[99999] flex max-h-[60vh] w-[360px] max-w-[calc(100vw-3rem)] flex-col rounded-xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <h3 className="text-sm font-bold text-[#2b5356]">Highlight &amp; ghi chú của bạn</h3>
            <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600" title="Đóng">
              <X className="h-5 w-5" />
            </button>
          </div>

          {hs.length > 0 && ns.length > 0 && (
            <div className="flex border-b border-gray-100">
              <TabBtn id="highlights" label="Highlight" count={hs.length} Icon={Highlighter} />
              <TabBtn id="notes" label="Ghi chú" count={ns.length} Icon={StickyNote} />
            </div>
          )}

          <div className="overflow-y-auto px-4 py-3">
            <p className="mb-2 text-xs text-gray-400">Bấm vào một mục để nhảy tới vị trí trong bài.</p>

            {activeTab === 'highlights' && (
              <ul className="space-y-1">
                {[...hs].sort(byPart).map((h, i) => (
                  <li key={h.id || `h${i}`}>
                    <button
                      type="button"
                      onClick={() => goTo(h.text, h.part)}
                      className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-50"
                    >
                      <span
                        className="mt-1.5 h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: HL_COLORS[h.level] || HL_COLORS[1] }}
                        title={h.level === 2 ? 'Tô lần 2' : 'Tô lần 1'}
                      />
                      <span className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">
                        {h.part ? <span className="mr-1 text-gray-400">P{h.part}:</span> : null}
                        {(h.text || '').trim()}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {activeTab === 'notes' && (
              <ul className="space-y-1.5">
                {[...ns].sort(byPart).map((n, i) => (
                  <li key={n.id || `n${i}`}>
                    <button
                      type="button"
                      onClick={() => goTo(n.selectedText || n.text, n.part)}
                      className="block w-full rounded-md border-l-2 border-emerald-400 px-3 py-1.5 text-left hover:bg-gray-50"
                    >
                      {(n.selectedText || '').trim() && (
                        <div className="mb-0.5 whitespace-pre-wrap break-words text-xs italic text-gray-400">"{(n.selectedText || '').trim()}"</div>
                      )}
                      {(n.text || '').trim() && <div className="whitespace-pre-wrap break-words text-sm text-gray-800">{n.text}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
